import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Lock, CalendarCheck, MessageCircle, CheckCircle2, Bell } from "lucide-react";
import { useLang } from "./common";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STATUS = {
  logged: { en: "Logged — request ready the day after the visit", hi: "लॉग हो गया — विज़िट के अगले दिन रिक्वेस्ट तैयार" },
  ready: { en: "Ready — tap to send on WhatsApp", hi: "तैयार — WhatsApp पर भेजें" },
  sent: { en: "Review requested ✓", hi: "रिव्यू रिक्वेस्ट भेजा गया ✓" },
};

const nudgeMsg = (v, lang) => {
  const plant = v.plant || "";
  if (lang === "hi") {
    return `नमस्ते ${v.name} जी! Vivan Energia ने आपके ${plant} सोलर प्लांट का AMC विज़िट पूरा किया है — पैनल क्लीन और इलेक्ट्रिकल जाँच लिए गए। अगर आप सेवा से खुश हैं तो 5 स्टार रिव्यू ज़रूर भेजिए, यहीं WhatsApp पर लिख दीजिए। धन्यवाद!`;
  }
  return `Namaste ${v.name}! Your AMC visit for your ${plant} solar plant is complete — panels cleaned and electricals checked. If you are happy with our service, please send us a 5-star review right here on WhatsApp. Thank you! — Vivan Energia`;
};

const istToday = () => new Date(Date.now() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);

const VisitRow = ({ v, lang, markSent, g }) => (
  <div data-testid={`visit-row-${v.id}`} className="flex flex-wrap items-center justify-between gap-2 rounded-2xl bg-[#F8F9FA] border border-[#0F295B]/10 px-4 py-3">
    <div className="min-w-0">
      <p className="text-sm font-bold text-[#0F295B]">{v.name} · {v.plant} · {v.visit_date}</p>
      <p className="text-xs text-[#4A5B78]">{STATUS[v.status]?.[lang] || v.status}</p>
    </div>
    {v.status === "ready" ? (
      <a href={`https://wa.me/91${v.phone.replace(/\D/g, "")}?text=${encodeURIComponent(nudgeMsg(v, lang))}`} target="_blank" rel="noreferrer"
        onClick={() => markSent(v.id)} data-testid={`visit-send-${v.id}`}
        className="rounded-full bg-[#00A651] text-white font-bold text-xs px-4 py-2.5 flex items-center gap-1.5 hover:bg-[#008842] transition-colors">
        <MessageCircle className="w-3.5 h-3.5" /> {g.sendWa}
      </a>
    ) : v.status === "sent" ? (
      <CheckCircle2 className="w-5 h-5 text-[#00A651]" />
    ) : null}
  </div>
);

const VisitTracker = () => {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [code, setCode] = useState("");
  const [f, setF] = useState({ name: "", phone: "", visit_date: "", plant: "", email: "" });
  const [visits, setVisits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);

  const load = async (c) => {
    try {
      const r = await fetch(`${BACKEND}/api/visits?code=${encodeURIComponent(c)}`);
      if (!r.ok) return;
      setVisits(await r.json());
    } catch { /* ignore */ }
  };

  useEffect(() => { if (open && code) load(code); }, [open, code, lang]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setStatus(null);
    try {
      const r = await fetch(`${BACKEND}/api/visits`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, ...f }),
      });
      if (!r.ok) throw new Error();
      setStatus("ok");
      setF({ name: "", phone: "", visit_date: "", plant: "", email: "" });
      load(code);
    } catch {
      setStatus("err");
    }
    setBusy(false);
  };

  const markSent = async (id) => {
    try {
      await fetch(`${BACKEND}/api/visits/update`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code, id, status: "sent" }),
      });
      load(code);
    } catch { /* ignore */ }
  };

  const g = t.visits;
  const sorted = [...visits].sort((a, b) => (a.visit_date || "").localeCompare(b.visit_date || ""));
  const today = istToday();
  const upcoming = sorted.filter((v) => (v.visit_date || "") >= today);
  const past = sorted.filter((v) => (v.visit_date || "") < today);
  const inp = "w-full rounded-2xl bg-[#F8F9FA] border border-[#0F295B]/15 px-3.5 py-2.5 text-sm text-[#0F295B] placeholder:text-[#6B7A93] focus:outline-none focus:border-[#00A651]";

  return (
    <div className="mt-3">
      <button onClick={() => setOpen((o) => !o)} data-testid="visits-owner-toggle"
        className="text-xs font-bold text-[#4A5B78] flex items-center gap-1.5 hover:text-[#0F295B] transition-colors">
        <Lock className="w-3.5 h-3.5" /> {g.ownerToggle}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            data-testid="visits-panel" className="mt-3 rounded-3xl bg-white border border-[#0F295B]/10 p-5 space-y-4 overflow-hidden">
            <p className="font-display text-lg text-[#0F295B]">{g.title}</p>
            <div className="flex items-start gap-2 rounded-2xl bg-[#F39C12]/10 border border-[#F39C12]/40 px-4 py-3">
              <Bell className="w-4 h-4 text-[#F39C12] shrink-0 mt-0.5" />
              <p className="text-xs text-[#0F295B] leading-relaxed">{g.morningNote}</p>
            </div>
            <form onSubmit={submit} className="space-y-3" data-testid="visit-form">
              <input type="password" value={code} onChange={(e) => setCode(e.target.value)} placeholder={t.gallery.codePh} data-testid="visit-code" required className={inp} />
              <div className="grid sm:grid-cols-2 gap-2">
                <input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={g.custName} data-testid="visit-name" required className={inp} />
                <input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder={g.custPhone} data-testid="visit-phone" required className={inp} />
              </div>
              <div className="grid sm:grid-cols-3 gap-2">
                <input type="date" value={f.visit_date} onChange={(e) => setF({ ...f, visit_date: e.target.value })} data-testid="visit-date" required className={inp} />
                <select value={f.plant} onChange={(e) => setF({ ...f, plant: e.target.value })} data-testid="visit-plant" className={inp}>
                  {["3 kW", "4 kW", "5 kW", "6 kW", "7 kW", "8-10 kW"].map((p) => <option key={p}>{p}</option>)}
                </select>
                <input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder={g.custEmail} data-testid="visit-email" className={inp} />
              </div>
              <button type="submit" disabled={busy} data-testid="visit-submit"
                className="rounded-full bg-[#F39C12] text-[#0F295B] font-bold px-6 py-3 text-sm hover:bg-[#ffb13b] transition-colors disabled:opacity-50 flex items-center gap-2">
                <CalendarCheck className="w-4 h-4" /> {g.logVisit}
              </button>
              {status === "ok" && <p data-testid="visit-status" className="text-xs font-bold text-[#00A651]">{g.loggedOk}</p>}
              {status === "err" && <p data-testid="visit-status" className="text-xs font-bold text-red-600">{t.gallery.revErr}</p>}
            </form>
            {upcoming.length > 0 && (
              <div className="space-y-2" data-testid="visits-list">
                <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-[#4A5B78]">{g.upcoming}</p>
                {upcoming.map((v) => <VisitRow key={v.id} v={v} lang={lang} markSent={markSent} g={g} />)}
              </div>
            )}
            {past.length > 0 && (
              <div className="space-y-2" data-testid="visits-past">
                <p className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-[#4A5B78]">{g.past}</p>
                {past.map((v) => <VisitRow key={v.id} v={v} lang={lang} markSent={markSent} g={g} />)}
              </div>
            )}
            <p className="text-[11px] text-[#4A5B78] leading-relaxed">{g.visitNote}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VisitTracker;
